import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CircularProgress, Typography, Select, MenuItem, FormControl, InputLabel, Box, FormHelperText, Paper, Alert } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

interface ReleaseArtifact {
  name: string;
  asset_id: number;
  size: number;
  download_count: number;
}

interface SoftwareOption {
  id: string;
  label: string;
  available: boolean;
}

// Human-readable byte size, e.g. 327800000 -> "312.6 MB". Wheelhouses are
// 300-400 MB, so showing this up front sets expectations before the download.
const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// Classify a product release asset into a human label, or null to hide it (e.g.
// auxiliary files). PyIBR ships per-product Windows installers plus a shared
// Python wheel and, when built, a PDF manual -- so the selector is derived from
// whatever the chosen release actually contains rather than hard-coded.
export const productAssetLabel = (name: string, software: string): string | null => {
  const n = name.toLowerCase();
  if (n.endsWith('.exe')) {
    if (software === 'autotwe') return 'AutoTWE — Windows x64 installer';
    if (n.includes('autoid') || n.includes('auto-id')) return 'AutoID — Windows installer';
    if (n.includes('gauge')) return 'Gauge Placement — Windows installer';
    return `${name} — Windows installer`;
  }
  if (n.endsWith('.whl')) return `Python wheel — ${name}`;
  if (software === 'autotwe' && n.endsWith('.tar.gz')) return 'Python source archive';
  if (software === 'autotwe' && n.endsWith('-html.zip')) return 'Documentation (HTML archive)';
  if (n.endsWith('.pdf')) return 'Documentation (PDF)';
  return null;
};

// Installers first, then wheel, then docs -- the order most users want.
const productRank = (label: string): number =>
  label.includes('AutoID') ? 0
    : label.includes('Gauge') ? 1
      : label.startsWith('Python') ? 3
        : label.startsWith('Documentation') ? 4
          : 2;

const Downloads = () => {
  // Software selection (FEMORPH is always present; AFRL products appear only for
  // entitled users, as reported by the server -- the real gate is server-side).
  const [softwareOptions, setSoftwareOptions] = useState<SoftwareOption[]>([{ id: 'femorph', label: 'FEMORPH', available: true }]);
  const [software, setSoftware] = useState('femorph');

  const [tags, setTags] = useState<string[]>([]);
  const [artifacts, setArtifacts] = useState<ReleaseArtifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [version, setVersion] = useState('');
  const [assetType, setAssetType] = useState('');
  const [os, setOs] = useState('');
  const [pythonVersion, setPythonVersion] = useState('');
  const [availableStandaloneOS, setAvailableStandaloneOS] = useState<string[]>([]);
  const [productAssetId, setProductAssetId] = useState<number | ''>('');
  const [downloadLink, setDownloadLink] = useState('');
  const [selectedArtifact, setSelectedArtifact] = useState<ReleaseArtifact | null>(null);
  const [isArtifactsLoading, setIsArtifactsLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [error, setError] = useState('');

  const isProductFiles = software === 'pyibr' || software === 'autotwe';
  const softwareLabel = softwareOptions.find(s => s.id === software)?.label ?? 'FEMORPH';

  const assetTypeInfo = [
    {
      label: "Documentation",
      value: "Documentation",
      description: "The complete GUI documentation for FEMORPH as a PDF file. Contains detailed information about using the FEMORPH interface."
    },
    {
      label: "Standalone",
      value: "Standalone",
      description: "A self-contained executable package that runs without a separate Python installation. Pick your operating system for its system requirements."
    },
    {
      label: "Python Wheelhouse",
      value: "PythonWheelhouse",
      description: "Contains all necessary dependencies for installing FEMORPH in an isolated Python environment. Ideal for integration into existing Python setups."
    }
  ];

  // System requirements for the standalone build, shown only for the OS the
  // user actually picked -- nobody wants to read the other platform's notes.
  const standaloneOSRequirements: Record<string, string> = {
    Linux: "Ships as a tarball. Requires Ubuntu 18.04 / RHEL 8 or newer.",
    Windows: "Requires Windows 10 or newer."
  };

  // Fetch the release artifacts for a tag of the CURRENTLY selected software.
  // Every request carries ?software= so the server scopes it to the right repo
  // AND re-checks entitlement for restricted products.
  const fetchArtifacts = (tag: string, sw: string) => {
    setIsArtifactsLoading(true);
    return axios.get(`/api/get-release-artifacts/${tag}`, { params: { software: sw } })
      .then(response => { setArtifacts(response.data.artifacts); })
      .catch(err => {
        console.error('Error fetching artifacts for selected version:', err);
        setArtifacts([]);
        setError(`Could not load files for ${tag}. Please try again.`);
      })
      .finally(() => { setIsArtifactsLoading(false); });
  };

  const handleVersionChange = (event: SelectChangeEvent) => {
    const selectedTag = event.target.value;
    setVersion(selectedTag);
    // A different release may not carry the same OS/Python combinations, so a
    // carried-over selection could point at a file that no longer exists. Clear
    // them and let the artifacts drive what's offered again.
    setOs('');
    setPythonVersion('');
    setProductAssetId('');
    setError('');
    fetchArtifacts(selectedTag, software);
  };

  // check authorization on mount
  useEffect(() => {
    axios.get('/artifact/authorized')
      .then(() => setIsAuthorized(true))
      .catch(err => {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setIsAuthorized(false);
        } else {
          console.error('Authorization check failed:', err);
        }
      });
  }, []);

  // Discover which software this user may download. Restricted products enter the
  // selector when the server says it's available for this identity.
  useEffect(() => {
    axios.get('/api/software')
      .then(res => {
        const list: SoftwareOption[] = res.data?.software ?? [];
        if (list.length > 0) setSoftwareOptions(list);
      })
      .catch(err => { console.error('Software capability check failed:', err); });
  }, []);

  // Load tags whenever the selected software changes (initial mount included).
  // Resets all downstream selections so nothing carries across products.
  useEffect(() => {
    setIsLoading(true);
    setError('');
    setArtifacts([]);
    setAssetType('');
    setOs('');
    setPythonVersion('');
    setProductAssetId('');
    axios.get('/api/get-all-tags', { params: { software } })
      .then(tagResponse => {
        const allTags = tagResponse.data.filter((tag: string) => tag.startsWith('v'));
        const recentTags = allTags.slice(0, 10);
        setTags(recentTags);
        if (recentTags.length > 0) {
          const initialTag = recentTags[0];
          setVersion(initialTag);
          return axios.get(`/api/get-release-artifacts/${initialTag}`, { params: { software } });
        } else {
          setVersion('');
          throw new Error('No valid tags found');
        }
      })
      .then(artifactResponse => { setArtifacts(artifactResponse?.data.artifacts ?? []); })
      .catch(err => {
        console.error('Error fetching data:', err);
        setError('Could not load releases right now. Please try again later.');
      })
      .finally(() => { setIsLoading(false); });
  }, [software]);

  useEffect(() => {
    // Determine available OS options for Standalone assets (FEMORPH only)
    const standaloneOSOptions = new Set<string>();
    artifacts.forEach(artifact => {
      // Standalone installers are the frozen builds, named `FEMORPH_*` (e.g.
      // FEMORPH_v1.18.3_Win64.exe). The `femorph_gui-<ver>.tar.gz` sdist is NOT
      // a standalone -- keying on the prefix stops Linux from offering a phantom
      // standalone (the sdist) when no Linux installer is built.
      if (!artifact.name.startsWith('FEMORPH_')) {
        return;
      }
      if (artifact.name.endsWith('.tar.gz')) {
        standaloneOSOptions.add('Linux');
      } else if (artifact.name.endsWith('.exe')) {
        standaloneOSOptions.add('Windows');
      }
    });
    setAvailableStandaloneOS(Array.from(standaloneOSOptions));

    // Auto-populate if only one OS option is available
    const osOptions = Array.from(standaloneOSOptions);
    if (osOptions.length === 1) {
      setOs(osOptions[0]);
    } else {
      setOs(''); // Reset the OS selection when multiple or no options are available
    }

  }, [artifacts]);

  // Product download choices, derived from whatever the selected release ships.
  const productChoices = useMemo(() => {
    return artifacts
      .map(a => ({ artifact: a, label: productAssetLabel(a.name, software) }))
      .filter((c): c is { artifact: ReleaseArtifact; label: string } => c.label !== null)
      .sort((a, b) => productRank(a.label) - productRank(b.label));
  }, [artifacts, software]);

  // Default the product selection to the first (an installer) once a release
  // loads, and keep it valid across version changes.
  useEffect(() => {
    if (!isProductFiles) return;
    if (productChoices.length === 0) {
      if (productAssetId !== '') setProductAssetId('');
      return;
    }
    if (!productChoices.some(c => c.artifact.asset_id === productAssetId)) {
      setProductAssetId(productChoices[0].artifact.asset_id);
    }
  }, [isProductFiles, productChoices, productAssetId]);

  // Python versions available FOR THE SELECTED OS (FEMORPH wheelhouse). Windows
  // and Linux wheelhouses can ship different Python versions, so a single
  // global list let a user pick a version that doesn't exist for their OS and
  // resolve to nothing. Derived from the artifacts (+ os) rather than state.
  const availablePythonVersions = useMemo(() => {
    const versions = new Set<string>();
    const osLower = os.toLowerCase();
    artifacts.forEach(artifact => {
      if (!artifact.name.includes('wheelhouse')) return;
      if (os && !artifact.name.toLowerCase().includes(osLower)) return;
      const match = artifact.name.match(/-(\d+\.\d+)\.zip$/);
      if (match) versions.add(match[1]);
    });
    return Array.from(versions).sort((a, b) => {
      const [majorA, minorA] = a.split('.').map(Number);
      const [majorB, minorB] = b.split('.').map(Number);
      return majorA === majorB ? minorA - minorB : majorA - majorB;
    });
  }, [artifacts, os]);

  // Keep the Python version valid for the current OS and default it to the
  // latest available.
  useEffect(() => {
    if (assetType !== 'PythonWheelhouse' || !os) return;
    if (availablePythonVersions.length === 0) {
      if (pythonVersion) setPythonVersion('');
    } else if (!availablePythonVersions.includes(pythonVersion)) {
      setPythonVersion(availablePythonVersions[availablePythonVersions.length - 1]);
    }
  }, [assetType, os, availablePythonVersions, pythonVersion]);

  const updateDownloadLink = () => {
    let artifact: ReleaseArtifact | undefined;

    if (isProductFiles) {
      artifact = artifacts.find(a => a.asset_id === productAssetId);
    } else if (assetType === 'Standalone') {
      artifact = artifacts.filter(a => a.name.startsWith('FEMORPH_') && a.name.endsWith(os === 'Linux' ? '.tar.gz' : '.exe'))[0];
    } else if (assetType === 'PythonWheelhouse') {
      const osLower = os.toLowerCase();
      artifact = artifacts.filter(a => a.name.includes('wheelhouse') && a.name.includes(pythonVersion) && a.name.toLowerCase().includes(osLower))[0];
    } else if (assetType === 'Documentation') {
      artifact = artifacts.filter(a => a.name.endsWith('.pdf'))[0];
    }

    // Always set (or clear) the link and the resolved file from the current
    // selection. Restricted links carry ?software= so the proxy scopes and re-
    // checks entitlement server-side.
    setSelectedArtifact(artifact ?? null);
    if (artifact) {
      const base = `/artifact/${encodeURIComponent(version)}/${artifact.asset_id}`;
      setDownloadLink(isProductFiles ? `${base}?software=${software}` : base);
    } else {
      setDownloadLink('');
    }
  };

  useEffect(updateDownloadLink, [artifacts, assetType, os, pythonVersion, version, software, productAssetId]);

  const selectedAssetInfo = assetTypeInfo.find(a => a.value === assetType);
  const isDownloadEnabled = isProductFiles
    ? productAssetId !== ''
    : assetType && (assetType !== 'Standalone' || os) && (assetType !== 'PythonWheelhouse' || (pythonVersion && os));
  const downloadReady = Boolean(isAuthorized && isDownloadEnabled && downloadLink && selectedArtifact);

  // Tell the user WHY the button is disabled instead of leaving it dead.
  const downloadHint =
    !isAuthorized ? '' :
    isProductFiles ? (productChoices.length === 0 ? 'No downloadable files in this release.' : (productAssetId === '' ? 'Select a file to continue.' : (!downloadLink ? 'No file matches this selection.' : ''))) :
    !assetType ? 'Select an asset type to continue.' :
    (assetType === 'Standalone' && !os) ? 'Select an OS to continue.' :
    (assetType === 'PythonWheelhouse' && !os) ? 'Select an OS to continue.' :
    (assetType === 'PythonWheelhouse' && !pythonVersion) ? 'Select a Python version to continue.' :
    !downloadLink ? 'No file matches this selection for the chosen version.' :
    '';

  const showOverlay = isLoading || isArtifactsLoading;
  const showSoftwareSelector = softwareOptions.filter(s => s.available).length > 1;

  return (
    <Box sx={{ position: 'relative', p: 2 }}>
      {showOverlay && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            zIndex: 1300
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{
          maxWidth: 500,
          mx: 'auto',
          my: 4,
          p: 4,
          borderRadius: 2,
          opacity: isArtifactsLoading ? 0.5 : 1,
          transition: 'opacity 0.2s ease'
        }}
      >
        <Typography variant="h5" align="center" sx={{ marginBottom: '20px', fontWeight: 600 }}>
          Download {softwareLabel} Assets
        </Typography>

        {error && (
          <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>
        )}

        {showSoftwareSelector && (
          <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
            <FormControl fullWidth>
              <InputLabel>Software</InputLabel>
              <Select
                value={software}
                label="Software"
                onChange={e => setSoftware(e.target.value)}
              >
                {softwareOptions.filter(s => s.available).map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
          <FormControl fullWidth>
            <InputLabel>Version</InputLabel>
            <Select
              value={version}
              label="Version"
              onChange={handleVersionChange}
            >
              {tags.map(tag => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isProductFiles ? (
          <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
            <FormControl fullWidth>
              <InputLabel>File</InputLabel>
              <Select
                value={productAssetId === '' ? '' : String(productAssetId)}
                label="File"
                onChange={e => setProductAssetId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                {productChoices.map(({ artifact, label }) => (
                  <MenuItem key={artifact.asset_id} value={String(artifact.asset_id)}>{label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose a {softwareLabel} installer, Python package, or documentation file.
              </FormHelperText>
            </FormControl>
          </Box>
        ) : (
          <>
            <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
              <FormControl fullWidth>
                <InputLabel>Asset Type</InputLabel>
                <Select
                  value={assetType}
                  label="Asset Type"
                  onChange={e => setAssetType(e.target.value)}
                >
                  {assetTypeInfo.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
                {selectedAssetInfo && (
                  <FormHelperText>{selectedAssetInfo.description}</FormHelperText>
                )}
              </FormControl>
            </Box>
            {assetType === 'Standalone' && availableStandaloneOS.length > 0 && (
              <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
                <FormControl fullWidth>
                  <InputLabel>OS</InputLabel>
                  <Select
                    value={os}
                    label="OS"
                    onChange={e => setOs(e.target.value)}
                  >
                    {availableStandaloneOS.map(osOption => (
                      <MenuItem key={osOption} value={osOption}>{osOption}</MenuItem>
                    ))}
                  </Select>
                  {os && standaloneOSRequirements[os] && (
                    <FormHelperText>{standaloneOSRequirements[os]}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}
            {assetType === 'PythonWheelhouse' && (
              <>
                <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
                  <FormControl fullWidth>
                    <InputLabel>OS</InputLabel>
                    <Select
                      value={os}
                      label="OS"
                      onChange={e => setOs(e.target.value)}
                    >
                      <MenuItem value="Linux">Linux</MenuItem>
                      <MenuItem value="Windows">Windows</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 120, marginBottom: '20px' }}>
                  <FormControl fullWidth>
                    <InputLabel>Python Version</InputLabel>
                    <Select
                      value={pythonVersion}
                      label="Python Version"
                      onChange={e => setPythonVersion(e.target.value)}
                    >
                      {availablePythonVersions.map(v => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </>
        )}

        <Tooltip
          title={
            !isAuthorized
              ? 'Your domain is not authorized to download artifacts.'
              : ''
          }
        >
          <Box>
            <Paper
              component="a"
              href={downloadReady ? downloadLink : undefined}
              variant="outlined"
              aria-disabled={!downloadReady}
              aria-label={downloadReady && selectedArtifact ? `Download ${selectedArtifact.name}` : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                textDecoration: 'none',
                color: 'inherit',
                cursor: downloadReady ? 'pointer' : 'default',
                borderColor: downloadReady ? 'primary.main' : 'divider',
                opacity: downloadReady ? 1 : 0.6,
                transition: 'background-color .15s ease, box-shadow .15s ease, color .15s ease, border-color .15s ease',
                '&:hover': downloadReady
                  ? { backgroundColor: 'primary.main', color: 'primary.contrastText', boxShadow: 4 }
                  : undefined,
              }}
            >
              <Box sx={{ fontSize: 30, lineHeight: 1 }} aria-hidden>📦</Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                {selectedArtifact ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                      {selectedArtifact.name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {formatBytes(selectedArtifact.size)}
                      {selectedArtifact.download_count > 0
                        ? ` · ${selectedArtifact.download_count} download${selectedArtifact.download_count === 1 ? '' : 's'}`
                        : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Choose an asset to download
                  </Typography>
                )}
              </Box>
              <DownloadRoundedIcon sx={{ opacity: downloadReady ? 1 : 0.4 }} />
            </Paper>
          </Box>
        </Tooltip>
        {downloadHint && (
          <FormHelperText sx={{ textAlign: 'center', marginTop: '8px' }}>
            {downloadHint}
          </FormHelperText>
        )}

      </Paper>
    </Box>
  );
};

export default Downloads;
